/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {Button, createStyles, StyleRules, Theme} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';
import React, {memo} from 'react';
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import Carousel from "react-material-ui-carousel";
import CarouselItem from "./CarouselItem";
import {asset} from "../../utils/asset";

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type HomeClassKey = 'root' | 'banner' | 'hero' | 'left' | 'headline' | 'sub' | 'buttons' | 'buttonPrimary' | 'buttonSecondary' | 'right' | 'carousel';

const useStyles = makeStyles<Theme, {}, HomeClassKey>(
  (): StyleRules<HomeClassKey> =>
    createStyles({
      root: {},
      banner: {
        display: 'block',
        margin: '0 auto',
        padding: '0 0 8px',
        // Both width and height must be `auto` for the browser to solve
        // them together against the image's intrinsic ratio — capping via
        // max-width/max-height only (not an explicit height) is what keeps
        // it proportional. The previous explicit `height` + `maxWidth: 80%`
        // combination broke on narrow screens: once the 80% cap clipped the
        // width, height stayed fixed instead of following it, squashing the
        // banner out of its native ratio.
        width: 'auto',
        height: 'auto',
        maxWidth: '80%',
        maxHeight: 'clamp(80px, 15vh, 160px)'
      },
      hero: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'clamp(24px, 4vw, 56px)',
        padding: 'clamp(20px, 4vh, 48px) clamp(20px, 4vw, 56px) clamp(48px, 8vh, 96px)'
      },
      left: {
        flex: '1 1 360px',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px'
      },
      headline: {
        margin: 0,
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(28px, 3.6vw, 44px)',
        lineHeight: 1.18,
        letterSpacing: '-.01em',
        maxWidth: '19ch'
      },
      sub: {
        margin: 0,
        maxWidth: '48ch',
        fontSize: '14.5px',
        lineHeight: 1.7,
        color: 'var(--color-text-muted)'
      },
      buttons: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '14px',
        marginTop: '6px'
      },
      buttonPrimary: {
        background: 'var(--color-text)',
        color: 'var(--color-bg)',
        border: 'none',
        borderRadius: 0,
        padding: '12px 22px',
        fontFamily: 'var(--font-body)',
        fontSize: '11.5px',
        fontWeight: 500,
        letterSpacing: '.16em',
        textTransform: 'uppercase',
        '&:hover': {
          background: 'var(--color-text-strong)'
        }
      },
      buttonSecondary: {
        background: 'none',
        color: 'rgba(232,230,226,.7)',
        border: '1px solid var(--color-border)',
        borderRadius: 0,
        padding: '12px 22px',
        fontFamily: 'var(--font-body)',
        fontSize: '11.5px',
        letterSpacing: '.16em',
        textTransform: 'uppercase',
        '&:hover': {
          borderColor: 'rgba(255,255,255,.55)',
          color: 'var(--color-text-strong)'
        }
      },
      right: {
        flex: '1 1 480px'
      },
      carousel: {}
    }),
  {
    name: 'Home'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type HomeProps = {
  className?: string;
};

export default memo(({className}: HomeProps) => {
  // hooks
  const classes = useStyles({});
  const {t, i18n} = useTranslation('common');

  return (
    <div className={classNames(classes.root, className)}>
      <img className={classes.banner} src={asset('files/img/banniere.png')} alt="Pierre Castay"/>

      <div className={classes.hero}>
        <div className={classes.left}>
          <p className={classes.headline}>{t('home.text.part3')}</p>
          <p className={classes.sub}>{t('home.text.part1')} {t('home.text.part2')}</p>
          <div className={classes.buttons}>
            <Link to="/galleries">
              <Button className={classes.buttonPrimary} disableRipple>{t('home.buttons.photos')}</Button>
            </Link>
            <Link to="/expos">
              <Button className={classes.buttonSecondary} disableRipple>{t('home.buttons.expos')}</Button>
            </Link>
          </div>
        </div>

        <div className={classes.right}>
          {/* animation='fade' (not 'slide'): react-material-ui-carousel's slide
              animation bakes a translateX into an inline style off a one-time
              DOM measurement, and a language switch re-rendering the whole
              tree leaves it permanently stuck off-screen — a pre-existing bug
              in the (unmaintained) library, not React-18-safe. fade only
              animates opacity, so it isn't affected; Comments.tsx's carousel
              already uses it for the same reason. Keyed by language as a
              belt-and-braces reset of the carousel's internal index state. */}
          <Carousel key={i18n.language} className={classes.carousel} animation='fade' interval={7000} timeout={800} >
            <CarouselItem id={0} title={t('home.nature')} pictureLeft={'files/img/slides/img_01.jpg'} pictureRight={'files/img/slides/img_02.jpg'} />
            <CarouselItem id={1} title={t('home.urban')} pictureLeft={'files/img/slides/img_03.jpg'} pictureRight={'files/img/slides/img_04.jpg'} />
            <CarouselItem id={2} title={t('home.myWorld')} pictureLeft={'files/img/slides/img_05.jpg'} pictureRight={'files/img/slides/img_06.jpg'} />
          </Carousel>
        </div>
      </div>
    </div>
  );
});
